/**
 * Endereços dos outros sistemas Sischef (retaguarda, PDV, analytics…).
 *
 * A fonte é o `menu-sistemas.json` publicado pelo PDV — o mesmo arquivo que
 * alimenta o menu "trocar de sistema". Ele existe porque o host muda por
 * ambiente e por sistema, e um arquivo central evita ter que rebuildar cada
 * front quando um endereço muda.
 *
 * O carregamento é cacheado no módulo: a promessa é criada uma vez e
 * compartilhada por todo mundo que pedir, então abrir o menu e montar um link
 * numa tela não geram dois downloads.
 */

import { useEffect, useState } from 'react';

export interface HostSistema {
  production: string;
  development: string;
}

export interface Sistema {
  id: string;
  nome: string;
  descricao: string;
  icone: string;
  hostKey: string;
  path: string;
  target: string;
  roles: string[];
  tokenParam: string | null;
  tokenPath?: string | null;
  notes?: string;
}

export interface MenuSistemasConfig {
  host: Record<string, HostSistema>;
  sistemas: Sistema[];
}

export type AmbienteSistemas = 'production' | 'development';

/** Chave do Sischef Retaguarda (Gerencial) no mapa de hosts. */
export const HOST_RETAGUARDA = 'retaguarda';

/**
 * Atendimento — mesmo destino do botão de fone do Sischef Retaguarda
 * (`template-my-mui.xhtml`). Vai fixo em produção, e não pelo
 * `menu-sistemas.json` como os outros links de sistema: o botão de suporte é
 * justamente o que o usuário procura quando alguma coisa não carregou, então
 * ele não pode sumir junto com o mapa de hosts.
 */
export const URL_HELPDESK = 'https://pdv.sischef.com/helpdesk';

let carregamento: Promise<MenuSistemasConfig | null> | null = null;

export function ambienteAtual(): AmbienteSistemas {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' ? 'development' : 'production';
}

export function carregarMenuSistemas(): Promise<MenuSistemasConfig | null> {
  carregamento ??= buscar();
  return carregamento;
}

async function buscar(): Promise<MenuSistemasConfig | null> {
  // Em produção sempre do host oficial. Localhost só em dev (com fallback pro oficial).
  const urls =
    ambienteAtual() === 'development'
      ? ['http://localhost:3005/menu-sistemas.json', 'https://pdv.sischef.com/menu-sistemas.json']
      : ['https://pdv.sischef.com/menu-sistemas.json'];

  for (const url of urls) {
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return (await res.json()) as MenuSistemasConfig;
    } catch (err) {
      console.warn(`Falha ao carregar de ${url}:`, err instanceof Error ? err.message : 'erro desconhecido');
    }
  }

  console.error('Falha ao carregar menu-sistemas.json de todas as fontes');
  // Sem cache do fracasso: a próxima chamada tenta de novo (pode ter sido rede).
  carregamento = null;
  return null;
}

/** Base de um sistema no ambiente atual, ou `null` se o mapa não trouxer o host. */
export function hostDoSistema(config: MenuSistemasConfig | null, chave: string): string | null {
  const host = config?.host[chave]?.[ambienteAtual()];
  return host ? host.replace(/\/+$/, '') : null;
}

/** Monta a URL de uma tela de outro sistema (`null` enquanto o mapa não chegou). */
export function urlDoSistema(config: MenuSistemasConfig | null, chave: string, caminho: string): string | null {
  const host = hostDoSistema(config, chave);
  return host ? `${host}/${caminho.replace(/^\/+/, '')}` : null;
}

/**
 * URL de uma tela de outro sistema, resolvida em background. Vale `null`
 * enquanto o mapa não chegou (ou se ele não trouxer o host) — quem usa deve
 * esconder o link nesse caso, em vez de mostrar um botão que não leva a lugar
 * nenhum.
 */
export function useUrlSistema(chave: string, caminho: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void carregarMenuSistemas().then((config) => {
      if (vivo) setUrl(urlDoSistema(config, chave, caminho));
    });
    return () => {
      vivo = false;
    };
  }, [chave, caminho]);

  return url;
}
